'use client';

import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import Icon from './Icon';

export type SlideItem = {
  id: string;
  content: ReactNode;
};

export type ProductSliderProps = {
  items: SlideItem[];
  autoPlayInterval?: number;
  showControls?: boolean;
  showIndicators?: boolean;
  showProgressBar?: boolean;
  /**
   * Tailwind class(es) that dictate the aspect ratio of the slider container.
   * This was the original prop name and will still work for ratio‑based layouts
   * (e.g. `aspect-16/6 md:aspect-21/7`).  The value is applied to an inner
   * wrapper.  If you prefer a fixed height instead of a ratio you can use
   * `heightClass` instead and leave this string empty.
   */
  aspectRatio?: string;
  /**
   * A set of Tailwind utility classes that set the container height directly.
   * When provided the outermost wrapper will include these (usually
   * something like `h-[30vh] md:h-[50vh]` or simply `h-full`).  This is useful
   * when you want the slider to match the height of an existing image
   * carousel and avoid hard‑coded aspect ratios.
   */
  heightClass?: string;
  className?: string;
};

/**
 * A very lightweight carousel used in several places throughout the theme.
 *
 * The slider's height can either be controlled by an aspect‑ratio class or a
 * set of explicit height utilities.  `aspectRatio` keeps the old behaviour and
 * will usually be set to something like `aspect-16/6` so the component has a
 * predictable height even if its parent is not sized.  When you need the
 * carousel to match the height of an existing image slider (as in the original
 * home banner) supply `heightClass` (e.g. `h-[30vh] md:h-[50vh]`) and the outer
 * container will stretch to that value; the slides are also forced to fill the
 * height so the transition effect maintains full coverage.
 */
const ProductSlider = ({
  items = [],
  autoPlayInterval = 5000,
  showControls = true,
  showIndicators = true,
  showProgressBar = true,
  aspectRatio = '',
  heightClass = '',
  className = '',
}: ProductSliderProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // track computed height of current slide when auto-sizing is active
  const [containerHeight, setContainerHeight] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);

  /**
   * Determine the height based on the current slide's image (if present) or
   * fallback to the slide's bounding rect.  Using the image's natural
   * aspect ratio ensures the container always matches the full image without
   * cropping.  We need the containerRef for width calculations.
   */
  const measureActiveSlide = useCallback(() => {
    if (heightClass) return; // explicit height wins
    const active = slideRefs.current[currentIndex];
    const container = containerRef.current;
    if (!active || !container) return;

    let newHeight: number | null = null;
    const img = active.querySelector('img') as HTMLImageElement | null;
    if (img && img.naturalWidth) {
      const containerWidth = container.getBoundingClientRect().width;
      newHeight = (img.naturalHeight / img.naturalWidth) * containerWidth;
    } else {
      const rect = active.getBoundingClientRect();
      newHeight = rect.height;
    }
    if (newHeight !== null) {
      setContainerHeight(newHeight);
    }
  }, [currentIndex, heightClass]);

  // observe the current slide or its image for size changes
  useEffect(() => {
    if (heightClass) return;
    const active = slideRefs.current[currentIndex];
    if (!active) return;

    measureActiveSlide();
    const target = (active.querySelector('img') as Element) || active;
    const ro = new ResizeObserver(measureActiveSlide);
    ro.observe(target);
    return () => {
      ro.disconnect();
    };
  }, [currentIndex, items, heightClass, measureActiveSlide]);

  // recalc on window resize as well
  useEffect(() => {
    if (heightClass) return;
    window.addEventListener('resize', measureActiveSlide);
    return () => window.removeEventListener('resize', measureActiveSlide);
  }, [measureActiveSlide, heightClass]);

  // measure before first paint so we don't flash a collapsed container
  useLayoutEffect(() => {
    if (heightClass) return;
    measureActiveSlide();
  }, [measureActiveSlide, heightClass]);

  const goToNext = useCallback(() => {
    if (!isTransitioning && items.length > 0) {
      setIsTransitioning(true);
      setCurrentIndex((prev) => (prev + 1) % items.length);
      setTimeout(() => setIsTransitioning(false), 500);
    }
  }, [items.length, isTransitioning]);

  const goToPrevious = useCallback(() => {
    if (!isTransitioning && items.length > 0) {
      setIsTransitioning(true);
      setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
      setTimeout(() => setIsTransitioning(false), 500);
    }
  }, [items.length, isTransitioning]);

  const goToSlide = useCallback(
    (index: number) => {
      if (!isTransitioning && index !== currentIndex) {
        setIsTransitioning(true);
        setCurrentIndex(index);
        setTimeout(() => setIsTransitioning(false), 500);
      }
    },
    [currentIndex, isTransitioning],
  );

  // Auto-play functionality
  useEffect(() => {
    if (items.length <= 1 || isHovered) return;

    const interval = setInterval(() => {
      goToNext();
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [items.length, isHovered, autoPlayInterval, goToNext]);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div
      // outer container: supports explicit height, plus any user supplied
      // class names.  when auto-sizing is active we write the calculated
      // `containerHeight` inline so the layout can expand.  a ref is kept
      // for width measurements.
      ref={containerRef}
      // ensure we have a small minimum in case the image hasn't measured
      // yet; this prevents a momentary collapse when the component first
      // renders.  Users can override with heightClass if they need a
      // different minimum.
      className={`relative w-full overflow-hidden bg-gray-100 min-h-[20vh] ${heightClass} ${className}`}
      style={containerHeight ? { height: `${containerHeight}px` } : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Slider Container */}
      <div className={`relative w-full ${heightClass ? 'h-full' : aspectRatio}`}>
        {/* Slides */}
        {items.map((item, index) => (
          <div
            key={item.id}
            ref={(el) => {
              slideRefs.current[index] = el;
            }}
            // absolute positioning is still used for the slide
            // animation, but the height is driven by the outer
            // container now.
            className={`absolute inset-0 transition-all duration-500 ease-in-out ${
              index === currentIndex ? 'opacity-100 translate-x-0 z-10' : index < currentIndex ? 'opacity-0 -translate-x-full z-0' : 'opacity-0 translate-x-full z-0'
            }`}
          >
            {item.content}
          </div>
        ))}
      </div>

      {/* Navigation Controls */}
      {showControls && items.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            disabled={isTransitioning}
            className="cursor-pointer absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-white text-gray-600 rounded-full p-2 md:p-3 shadow-lg transition-all duration-200 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Previous slide"
          >
            <Icon name="chevron-left" className="w-5 h-5 md:w-6 md:h-6" />
          </button>
          <button
            onClick={goToNext}
            disabled={isTransitioning}
            className="cursor-pointer absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-white text-gray-600 rounded-full p-2 md:p-3 shadow-lg transition-all duration-200 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Next slide"
          >
            <Icon name="chevron-right" className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        </>
      )}

      {/* Indicators */}
      {showIndicators && items.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {items.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              disabled={isTransitioning}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex ? 'w-8 md:w-10 h-2 md:h-2.5 bg-white' : 'w-2 md:w-2.5 h-2 md:h-2.5 bg-white/60 hover:bg-white/80'
              } disabled:cursor-not-allowed`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Progress Bar */}
      {showProgressBar && items.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-300/30 z-20">
          <div
            className="h-full bg-white/60 transition-all ease-linear"
            style={{
              width: isHovered ? '0%' : '100%',
              transitionDuration: isHovered ? '0s' : `${autoPlayInterval}ms`,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ProductSlider;
